import { Fields } from "../fields/fields";
import { LoanTypeViewModel } from "./loan-type.view-model";
import { FieldViewModel } from "../fields/field.view-model";
import { LoanTypeDeleteButton } from "./loan-type-delete-button";
import {
	Flex,
	Box,
	Accordion,
	AccordionItem,
	IconButton,
	AccordionButton,
	Stack,
	Badge,
	AccordionIcon,
	AccordionPanel,
	Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface LoanTypeProps {
	loanType: LoanTypeViewModel;
	onEdit: (loanType: LoanTypeViewModel) => void;
	onUpdate: (loanType: LoanTypeViewModel) => void;
	onDelete: (loanType: LoanTypeViewModel) => void;
}

export function LoanType({
	loanType,
	onEdit,
	onUpdate,
	onDelete
}: LoanTypeProps) {
	function handleOnFieldAdded(field: FieldViewModel) {
		loanType.fields = [...loanType.fields, field];
		onUpdate(loanType);
	}

	function handleFieldsReordered(fields: FieldViewModel[]) {
		loanType.fields = fields;
		onUpdate(loanType);
	}

	function handleHideFromBorrowers(fields: FieldViewModel[]) {
		loanType.fields = fields;
		onUpdate(loanType);
	}

	function handleOnFieldUpdated(field: FieldViewModel) {
		loanType.fields = loanType.fields.map((f) =>
			f.id === field.id ? field : f
		);
		onUpdate(loanType);
	}

	function handleOnFieldDeleted(field: FieldViewModel) {
		loanType.fields = loanType.fields.filter((f) => f.id !== field.id);
		onUpdate(loanType);
	}

	function hasFieldsForApplication() {
		return loanType.fields?.some(
			(field: any) =>
				field.show_on_lender_application || field.show_on_borrower_application
		);
	}

	function hasFieldsForLoan() {
		return loanType.fields?.some(
			(field: any) => field.show_on_lender_loan || field.show_on_borrower_loan
		);
	}

	return (
		<>
			<Flex>
				<Box flex="1">
					<Accordion allowToggle flex="1">
						<AccordionItem
							key={loanType.id}
							borderColor="gray.50"
							borderTopWidth="0"
						>
							<Flex
								alignItems="center"
								_hover={{
									backgroundColor: "gray.25",
								}}
							>
								{/* ACTIONS */}
								<Box>
									<Flex>
										<Box>
											<IconButton
												onClick={() => onEdit(loanType)}
												aria-label="edit-field"
												variant="outline"
												size="sm"
												borderColor="#9E9AA3"
												color="#9E9AA3"
												_hover={{
													color: "black",
													borderColor: "black",
												}}
												icon={<FontAwesomeIcon icon={["far", "pencil"]} />}
											/>
										</Box>
									</Flex>
								</Box>

								{/* SECTION NAME */}
								<Box flex="1">
									<AccordionButton
										_hover={{
											backgroundColor: "none",
										}}
									>
										<Flex direction="column" alignItems="flex-start" flex="1">
											<Text color="black" fontWeight="700" fontSize="14px">
												{loanType.title}
											</Text>
											<Text fontSize="12px">
												{loanType.description === ""
													? "No description"
													: loanType.description}
											</Text>
										</Flex>

										<Stack direction="row" mr="4">
											<Badge
												variant="yellow"
												display={hasFieldsForApplication() ? "block" : "none"}
											>
												Application
											</Badge>

											<Badge
												variant="lightBlue"
												display={hasFieldsForLoan() ? "block" : "none"}
											>
												Loan
											</Badge>
										</Stack>

										<AccordionIcon />
									</AccordionButton>
								</Box>
							</Flex>

							{/* SECTION FIELDS */}
							<AccordionPanel pt={0}>
								<Fields
									fields={loanType.fields}
									section={null}
									loan_type={loanType}
									onFieldUpdated={handleOnFieldUpdated}
									onFieldDeleted={handleOnFieldDeleted}
									onFieldAdded={handleOnFieldAdded}
									onFieldsReordered={handleFieldsReordered}
									onHideFromBorrowers={handleHideFromBorrowers}
								/>
								<LoanTypeDeleteButton loanType={loanType} onDelete={onDelete} />
							</AccordionPanel>
						</AccordionItem>
					</Accordion>
				</Box>
			</Flex>
		</>
	);
}
